import {BASE_PROPS_SCHEMA_TYPE, DATA_TYPES} from '../constants/constants'
import type {DAL} from '@wix/document-manager-core'
import type {Pointer, RefInfo, SchemaFile, SchemaService, DSConfig, Experiment} from '@wix/document-services-types'
import _ from 'lodash'
import type {NamespaceSchema, Reference, SchemaCollection} from '../extensions/csave/serverProtocolTypes'
import {schemaConfigs} from '@wix/document-services-json-schemas'
const {getDefaultFriendlyReferencedMap} = schemaConfigs
interface CompNamespaceTypeValidation {
    isValid: boolean
    message?: string
}

const getCompNamespaceTypes = (compDefinition: any, namespace: string) => {
    switch (namespace) {
        case DATA_TYPES.prop:
            if (compDefinition.propertyTypes) {
                return [...compDefinition.propertyTypes, BASE_PROPS_SCHEMA_TYPE]
            }
            if (compDefinition.propertyType && !_.isArray(compDefinition.propertyType)) {
                return [compDefinition.propertyType, BASE_PROPS_SCHEMA_TYPE]
            }
            return [BASE_PROPS_SCHEMA_TYPE, '']
        case DATA_TYPES.design:
            if (compDefinition?.designDataTypes?.length) {
                return compDefinition.designDataTypes.concat('RefArray')
            }
            return ['']
        case DATA_TYPES.data:
            return compDefinition.dataTypes ?? ['']
        case DATA_TYPES.slots:
            return compDefinition.slotsDataType
    }
}

const validateCompNamespaceType = (
    dal: DAL,
    componentType: string,
    itemType: string,
    namespace: string
): CompNamespaceTypeValidation => {
    const {schema} = dal
    const componentDefinition = schema.getComponentDefinition(componentType)
    if (!componentDefinition) {
        return {isValid: true}
    }
    const types = getCompNamespaceTypes(componentDefinition, namespace)
    if (types && !_.includes(types, itemType)) {
        return {
            isValid: false,
            message: `${componentType} ${namespace}, should be one of the following [${types}], but got: ${
                itemType ? itemType : 'null (no item)'
            }`
        }
    }
    return {
        isValid: true
    }
}

const DEAD_MOBILE_COMPONENT_TYPE = 'to.be.removed.by.MobileMerge'

const isComponentDefinitionValid = (dal: DAL, pointer: Pointer, comp: any = {}) => {
    const {schema} = dal
    const {componentType} = comp
    const componentDefinition = schema.getComponentDefinition(componentType)
    return !!(componentDefinition || (pointer.type === 'MOBILE' && componentType === DEAD_MOBILE_COMPONENT_TYPE))
}

const mapReferencesToRefInfo = (references: Reference[]): RefInfo[] => {
    return references.map(({list, refOwner, referencedMap, ...reference}) => ({
        ...reference,
        isList: list,
        isRefOwner: refOwner,
        referencedMap: getDefaultFriendlyReferencedMap(referencedMap!)
    })) as RefInfo[]
}

const convertNamespaceToSchemaRecord = (namespace: NamespaceSchema): Record<string, SchemaFile> => {
    if (!namespace) {
        return {}
    }

    const result: Record<string, SchemaFile> = {}

    if (namespace.type && namespace.jsonSchema) {
        result[namespace.type] = namespace.jsonSchema as SchemaFile
    }

    if (namespace.derivedNamespaces && Array.isArray(namespace.derivedNamespaces)) {
        for (const derivedNamespaceData of namespace.derivedNamespaces) {
            if (derivedNamespaceData?.type && derivedNamespaceData.jsonSchema) {
                result[derivedNamespaceData.type] = derivedNamespaceData.jsonSchema as SchemaFile
            }
        }
    }

    return result
}

const getReferencesFromNamespace = (namespaceValue: NamespaceSchema): Record<string, Reference[]> => {
    const result: Record<string, Reference[]> = {}

    if (namespaceValue?.references && namespaceValue.type) {
        result[namespaceValue.type] = namespaceValue.references
    }

    if (namespaceValue?.derivedNamespaces && Array.isArray(namespaceValue.derivedNamespaces)) {
        for (const derivedNamespaceData of namespaceValue.derivedNamespaces) {
            if (derivedNamespaceData?.references && derivedNamespaceData.type) {
                result[derivedNamespaceData.type] = derivedNamespaceData.references
            }
        }
    }

    return result
}

const shouldSkipBuilderSchemaValidation = (dsConfig: DSConfig, experimentInstance: Experiment) => {
    return !!dsConfig.skipBuilderSchemaValidation && !!experimentInstance.isOpen('dm_sapiSkipBuilderSchemaValidation')
}

const registerSchemaCollection = (
    dsConfig: DSConfig,
    experimentInstance: Experiment,
    schemaService: SchemaService,
    schemaCollection: SchemaCollection
) => {
    const {id, definition, namespaces = {}} = schemaCollection
    //TODO: remove once schema-generator generates ML schemas
    if (namespaces.data && !namespaces.multilingualTranslations) {
        namespaces.multilingualTranslations = _.cloneDeep(namespaces.data)
    }
    const skipSchemaCoreRegistration = shouldSkipBuilderSchemaValidation(dsConfig, experimentInstance)

    if (definition?.type === 'LegacyComponent' && definition.builderTypes?.referredType) {
        const referredDefinition = schemaService.getDefinition(definition.builderTypes.referredType)
        referredDefinition.aliases.push(id!)
        schemaService.registerComponentSchemasCollection(
            definition!.builderTypes.referredType,
            {
                [definition.builderTypes.referredType]: referredDefinition
            },
            {},
            skipSchemaCoreRegistration
        )
        return
    }

    const convertedNamespaces = Object.entries(namespaces).reduce(
        (acc, [namespace, namespaceValue]: [string, NamespaceSchema]) => {
            acc[namespace] = convertNamespaceToSchemaRecord(namespaceValue)
            _.forEach(_.keys(acc[namespace]), schemaId => schemaService.markAsBuilder(schemaId!))
            return acc
        },
        {} as Record<string, Record<string, SchemaFile>>
    )

    schemaService.markAsBuilder(id!)
    schemaService.registerComponentSchemasCollection(
        id!,
        {
            [id!]: definition
        },
        convertedNamespaces,
        skipSchemaCoreRegistration
    )

    const allReferences = Object.entries(namespaces).reduce(
        (acc, [namespace, namespaceValue]: [string, NamespaceSchema]) => {
            const references = getReferencesFromNamespace(namespaceValue)
            acc[namespace] = acc[namespace] || {}
            Object.assign(acc[namespace], references)

            return acc
        },
        {} as Record<string, Record<string, Reference[]>>
    )

    for (const [namespace, typeToReferences] of Object.entries(allReferences)) {
        for (const [type, references] of Object.entries(typeToReferences)) {
            const refsInfo = mapReferencesToRefInfo(references)
            schemaService.addReferences(namespace, type, refsInfo)
        }
    }

    return allReferences
}

export {isComponentDefinitionValid, validateCompNamespaceType, CompNamespaceTypeValidation, registerSchemaCollection}
